// Produced-file vocabulary + dual-read helper for the multi-file task result model.
//
// IMPORTANT: this module is deep-imported by the web app, so it must stay free of
// node-only imports (no `path`, `fs`, etc.). Keep the few string helpers inline.
//
// The model has two independent axes:
//   - `format` — what kind of file it is (video, static image, layered PSD, ...).
//   - `role`   — the purpose it serves, which drives legacy-slot derivation
//                (primary -> `path`, poster -> `posterPath`, alternate -> no slot).
// Keeping them separate is deliberate: a still image can be `format: static` while
// serving `role: poster`, and the layered PSD is `format: layered, role: alternate`
// (it never had a legacy slot — it lived in the `data` bag).

/** The kind of file a render produced. */
export enum ProducedFileFormat {
	Video = 'video',
	Static = 'static',
	Layered = 'layered',
	Archive = 'archive', // HTML5 / bundle zip
	Pdf = 'pdf',
	Audio = 'audio',
	Gif = 'gif'
}

/** The purpose a file serves, which determines how legacy slots are derived. */
export enum ProducedFileRole {
	Primary = 'primary', // main deliverable -> legacy `path`
	Poster = 'poster', // still / thumbnail -> legacy `posterPath`
	Alternate = 'alternate' // extra output (layered, extra formats, gif) -> no legacy slot
}

/** A single produced file. `label` is required only when `format` repeats within a task. */
export interface ProducedFile {
	url: string;
	format: ProducedFileFormat;
	role: ProducedFileRole;
	label?: string;
	mime?: string;
}

/** Minimal shape the helper reads — avoids importing TaskResult (circular) and works for the web's plain result type too. */
interface ReadableResult {
	files?: ProducedFile[];
	path?: string;
	posterPath?: string;
	data?: any;
}

const VIDEO_EXTS = ['.mp4', '.webm', '.mov', '.m4v'];
const STATIC_EXTS = ['.png', '.jpg', '.jpeg', '.webp', '.svg'];
const AUDIO_EXTS = ['.mp3', '.wav'];

/** Lowercase file extension (incl. dot) from a URL, ignoring query string. Node-free. */
export function extensionFromUrl(url: string | undefined | null): string {
	if (!url) return '';
	const clean = url.split('?')[0];
	const lastSlash = clean.lastIndexOf('/');
	const name = lastSlash >= 0 ? clean.slice(lastSlash + 1) : clean;
	const dot = name.lastIndexOf('.');
	return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

/** Best-effort format for a legacy `path` URL, whose kind varies by render path (video/zip/pdf/...). */
function formatFromUrl(url: string): ProducedFileFormat {
	const ext = extensionFromUrl(url);
	if (VIDEO_EXTS.includes(ext)) return ProducedFileFormat.Video;
	if (ext === '.pdf') return ProducedFileFormat.Pdf;
	if (ext === '.zip') return ProducedFileFormat.Archive;
	if (ext === '.psd') return ProducedFileFormat.Layered;
	if (ext === '.gif') return ProducedFileFormat.Gif;
	if (AUDIO_EXTS.includes(ext)) return ProducedFileFormat.Audio;
	if (STATIC_EXTS.includes(ext)) return ProducedFileFormat.Static;
	return ProducedFileFormat.Video; // legacy `path` default (the historical video slot)
}

function dedupeByUrl(files: ProducedFile[]): ProducedFile[] {
	const seen = new Set<string>();
	const out: ProducedFile[] = [];
	for (const f of files) {
		if (!f || !f.url || seen.has(f.url)) continue;
		seen.add(f.url);
		out.push(f);
	}
	return out;
}

/**
 * The single dual-read entry point. Yields the produced files for any task result:
 *   1. the explicit `files` array when present (source of truth), else
 *   2. the legacy fixed slots (`path` -> primary, `posterPath` -> poster), plus
 *   3. outputs from the newer `data` bag (video/GIF/image/layered).
 * Deduped by URL so a result carrying both an array and overlapping slot/data URLs
 * never double-counts. Every migrated consumer reads files through this helper.
 */
export function filesFromResult(result: ReadableResult | undefined | null): ProducedFile[] {
	if (!result) return [];

	if (Array.isArray(result.files) && result.files.length > 0) {
		return dedupeByUrl(result.files);
	}

	const derived: ProducedFile[] = [];
	if (result.path) {
		derived.push({ url: result.path, format: formatFromUrl(result.path), role: ProducedFileRole.Primary });
	}
	const dataVideo = result.data?.video;
	if (dataVideo && typeof dataVideo === 'string') {
		derived.push({ url: dataVideo, format: ProducedFileFormat.Video, role: ProducedFileRole.Primary });
	}
	const animatedGif = result.data?.animatedGif;
	if (animatedGif && typeof animatedGif === 'string') {
		derived.push({
			url: animatedGif,
			format: ProducedFileFormat.Gif,
			// A GIF-only export is the main deliverable; when a video is also
			// present the GIF is an alternate. This preserves the release's
			// path -> GIF -> poster preference for legacy results.
			role: derived.some(file => file.role === ProducedFileRole.Primary)
				? ProducedFileRole.Alternate
				: ProducedFileRole.Primary
		});
	}
	const poster = result.posterPath ?? result.data?.image;
	if (poster && typeof poster === 'string') {
		derived.push({ url: poster, format: ProducedFileFormat.Static, role: ProducedFileRole.Poster });
	}
	const layered = result.data?.layeredFile;
	if (layered && typeof layered === 'string') {
		derived.push({ url: layered, format: ProducedFileFormat.Layered, role: ProducedFileRole.Alternate });
	}
	return dedupeByUrl(derived);
}

/**
 * Derive the legacy fixed slots from a files array so not-yet-migrated readers keep working.
 * Byte-identical to today's encoder output: the primary entry -> `path` (absent for
 * static-only tasks, exactly as today), the poster entry -> `posterPath`. Alternates
 * (layered, gif, extra formats) have no slot. Callers spread the result onto the task result.
 */
export function deriveLegacySlots(files: ProducedFile[]): { path?: string; posterPath?: string } {
	const slots: { path?: string; posterPath?: string } = {};
	const primary = files.find(f => f.role === ProducedFileRole.Primary);
	const poster = files.find(f => f.role === ProducedFileRole.Poster);
	if (primary) slots.path = primary.url;
	if (poster) slots.posterPath = poster.url;
	return slots;
}

/** True when two entries share a `format` but at least one lacks a distinguishing `label`. */
export function hasAmbiguousLabels(files: ProducedFile[]): boolean {
	const byFormat = new Map<ProducedFileFormat, ProducedFile[]>();
	for (const f of files) {
		const group = byFormat.get(f.format) || [];
		group.push(f);
		byFormat.set(f.format, group);
	}
	for (const group of byFormat.values()) {
		if (group.length > 1 && group.some(f => !f.label)) return true;
	}
	return false;
}
